// Import
import { Box, IconButton, Typography, useTheme } from "@mui/material";
import React, { useEffect, useMemo, useState, useRef } from "react";
import {
  InputCst,
  SelectCst,
  AutocompleteCst,
  MultiSelectCst,
  RadioCst,
  DataPickerCst,
  SingleCheckboxCst,
  InputAssociationChipsCst,
  MultiTextCst,
  SwitchCst,
} from "@components/InputCst";
import { ColumnInterface } from "@components/Table/types";
import Alert from "@mui/material/Alert";
import { Grid } from "@mui/material";
import { useTranslation } from "react-i18next";
import { defaultValidation } from "@utils/utilsValidators";
import DownloaderUploaderCst from "@components/DownloaderUploaderCst";
import InputChipsCst from "@components/InputCst/InpuctChipsCst";
import TimePickerCst from "@components/InputCst/TimePickerCst";
import { TextEditorCst } from "@components/TextEditorCst";
import { Association } from "src/interfaces/Common";
import { Delete, Save } from "@mui/icons-material";
import dayjs from "dayjs";
import ModalCst from "@components/ModalCst";
// Interface

interface SessionEditionFormCstInterface {
  row?: any;
  editIndex?: number;
  columns: ColumnInterface[];
  additionalColumns?: ColumnInterface[];
  actionsForForms?: ColumnInterface[];
  onSubmit: (sessionForm: any) => void;
  onReset?: () => void;
  onResetEdit?: () => void;
  formActionType?: string[];
  inputsException?: (col: any, sessionForm: any) => any;
  backButton?: () => any;
  getValues?: (accessorKey: string) => any;
  handleDelete?: (accessorKey: string, element: any) => void;
  handleAssociation?: (accessorKey: string, value: any) => void;
  setOpenAssociation?: (accessorKey: string, open: boolean) => void;
  association?: boolean | Association;
  isFaculties?: boolean;
  disabledConfig?: { view: string; accessoryKey: string };
  readOnly?: boolean;
  isCorporate?: boolean;
  deleteDisabled?: boolean;
  options?: any;
  setSessionForm?: any;
  sessionForm?: any;
  sessionsCount?: number;
  siteAssociation?: any;
  sessions?: any;
  mainForm?: any;
  isPublishFuture?: boolean;
  roomAssociation?: { id: number; label: string }[];
  facultiesAssociation?: { id: number; label: string }[];
  tutorsAssociation?: { id: number; label: string }[];
  producerAssociation?: { id: number; label: string }[];
  presenterAssociation?: { id: number; label: string }[];
  coorganizerAssociation?: { id: number; label: string }[];
  cohostAssociation?: { id: number; label: string }[];
  isNew?: boolean;
  isAdd?: boolean;
  setSiteAssociation?: (item: any) => void;
  setRoomAssociation?: (item: any) => void;
  isVirtual: boolean;
  confirmedDisabledTooltip?: string;
}

const MeetingSection = React.memo(
  ({ title, children }: { title: string; children: React.ReactNode }) => (
    <Grid item xs={12} sx={{ mb: 3 }}>
      <Box
        sx={{
          border: (theme) => `1px solid ${theme.palette.text.tertiary}`,
          p: 4,
          display: "flex",
          flexDirection: "column",
          borderRadius: 1,
          position: "relative",
        }}
      >
        <Box
          sx={{
            px: 1,
            position: "absolute",
            top: 0,
            left: 10,
            backgroundColor: (theme) => theme.palette.background.paper,
            transform: "translateY(-50%)",
            color: (theme) => theme.palette.text.primary,
          }}
        >
          {title}
        </Box>
        <Grid container spacing={3}>
          {children}
        </Grid>
      </Box>
    </Grid>
  ),
);

// eslint-disable-next-line no-empty-pattern
const SessionEditionFormCst: React.FC<SessionEditionFormCstInterface> = ({
  sessions,
  row,
  editIndex,
  columns,
  actionsForForms = [],
  inputsException,
  onSubmit,
  onReset,
  onResetEdit,
  backButton,
  getValues,
  handleDelete,
  handleAssociation,
  setOpenAssociation,
  association,
  readOnly,
  options,
  setSessionForm,
  sessionForm,
  sessionsCount,
  siteAssociation,
  mainForm,
  isPublishFuture,
  facultiesAssociation,
  tutorsAssociation,
  roomAssociation,
  producerAssociation,
  presenterAssociation,
  coorganizerAssociation,
  cohostAssociation,
  isNew,
  isAdd = false,
  setSiteAssociation,
  setRoomAssociation,
  isVirtual = false,
  confirmedDisabledTooltip,
}) => {
  const theme = useTheme();
  const { t } = useTranslation(["form"]);
  const [errorsessionForm, setErrorsessionForm] = React.useState<any>({});
  const [fileUploaded, setFileUploaded] = React.useState<any>(null);
  const [openErrorDatesSession, setOpenErrorDateSession] = useState(false);
  const [openErrorRoleSession, setOpenErrorRoleSession] = useState(false);
  const [endSession, setEndSession] = useState<boolean>(false);
  const [startSession24h, setStartSession24h] = useState<boolean>(false);
  const [startSession, setStartSession] = useState<boolean>(false);

  const getInitialData = () => {
    let initialState: any = {};
    columns?.forEach(({ accessorKey }) => {
      switch (accessorKey) {
        case "site":
          const isSiteValid =
            row.site && typeof row.site === "object" && row.site.id;
          const isSiteAlreadySet =
            Array.isArray(siteAssociation) && siteAssociation.length > 0;

          if (isSiteValid && !isSiteAlreadySet) {
            setSiteAssociation && setSiteAssociation([row.site]);
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "rooms":
          const isRoomValid =
            row.site && typeof row.rooms === "object" && row.rooms.id;
          const isRoomAlreadySet =
            Array.isArray(siteAssociation) && siteAssociation.length > 0;

          if (isRoomValid && !isRoomAlreadySet) {
            setSiteAssociation && setSiteAssociation([row.rooms]);
          }

          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
        case "tool":
          initialState = {
            ...initialState,
            [accessorKey]: sessionForm[accessorKey]
              ? sessionForm[accessorKey]
              : row[accessorKey],
          };
          break;
        case "trigger_external_services":
          initialState = {
            ...initialState,
            [accessorKey]:
              !!row["online_meeting_updated_at"] || row[accessorKey],
          };
          break;
        default:
          initialState = {
            ...initialState,
            [accessorKey]: row[accessorKey],
          };
          break;
      }
    });

    return initialState;
  };

  useEffect(() => {
    if (row && Object.keys(row).length > 0 && columns?.length > 0) {
      const hasMainFields =
        sessionForm.id !== undefined || sessionForm.start_date !== undefined;

      if (!hasMainFields) {
        const initialState = getInitialData();
        setSessionForm(initialState);
      }

      // Disable all columns if readOnly=true prop is passed
      if (readOnly) {
        columns?.forEach((column) => (column.disabled = true));
      }
    }
  }, []);

  useEffect(() => {
    const now = dayjs();
    if (row.end_date) {
      const endDate = dayjs(row.end_date);
      setEndSession(
        now.isAfter(endDate) &&
          isPublishFuture !== undefined &&
          !isPublishFuture,
      );
    } else {
      setEndSession(false);
    }
    if (row.start_date) {
      const startDate = dayjs(row.start_date);
      const isWithin24h =
        now.isBefore(startDate) && startDate.diff(now, "hour", true) <= 24;
      setStartSession24h(
        isWithin24h && isPublishFuture !== undefined && !isPublishFuture,
      );
    } else {
      setStartSession24h(false);
    }
    if (row.start_date) {
      const startDate = dayjs(row.start_date);
      setStartSession(
        now.isAfter(startDate) &&
          isPublishFuture !== undefined &&
          !isPublishFuture,
      );
    } else {
      setStartSession(false);
    }
  }, [row.end_date, row.start_date]);

  useEffect(() => {
    const currentSiteId = siteAssociation?.[0]?.id;
    const originalSiteId =
      row?.site?.id || (Array.isArray(row?.site) ? row.site[0]?.id : null);

    if (currentSiteId == null || originalSiteId == null) {
      return;
    }

    const isDifferent = currentSiteId != originalSiteId;

    if (isDifferent) {
      const currentRoomsStr = JSON.stringify(roomAssociation);
      const originalRoomsStr = JSON.stringify(row?.rooms);
      const isStillOnInitialRooms = currentRoomsStr === originalRoomsStr;

      const currentRoomsLength = roomAssociation?.length ?? 0;

      if (isStillOnInitialRooms && currentRoomsLength > 0) {
        if (setRoomAssociation) setRoomAssociation([]);

        setSessionForm((prev: any) => ({
          ...prev,
          site: siteAssociation,
          rooms: [],
        }));
      }
    }
  }, [siteAssociation, roomAssociation, row]);

  const onConfirm = () => {
    const { canBeSubmit, showError } = defaultValidation(columns, sessionForm);

    sessionForm = { ...row, ...sessionForm };

    const selectedStartDate = new Date(
      dayjs(sessionForm.start_date).toString(),
    ).getTime();
    const selectedEndDate = new Date(
      dayjs(sessionForm.end_date).toString(),
    ).getTime();
    let canContinue = true;

    if (sessions && !!sessions.length) {
      sessions.forEach((session: any, sessionIndex: number) => {
        const start = new Date(session.start_date).getTime();
        const end = new Date(session.end_date).getTime();

        if (editIndex && sessionIndex === editIndex - 1) return;

        canContinue =
          (selectedStartDate < start && selectedEndDate < start) ||
          (selectedStartDate > end && selectedEndDate > end);
      });

      //SE E' VIRTUALE DEVO CONTROLLARE CHE SIA DOCENTI CHE TUTOR ABBIANO UN RUOLO ASSEGNATO
      if (isVirtual && sessionForm["tool"] !== "OTHER") {
        const facultiesIds = (facultiesAssociation || []).map((f: any) =>
          Number(f.id),
        );

        const tutorsIds = (tutorsAssociation || []).map((t: any) =>
          Number(t.id),
        );

        const producerIds = (producerAssociation || []).map((p: any) =>
          Number(p.id),
        );

        const presenterIds = (presenterAssociation || []).map((p: any) =>
          Number(p.id),
        );

        const coorganizerIds = (coorganizerAssociation || []).map((c: any) =>
          Number(c.id),
        );

        const cohostIds = (cohostAssociation || []).map((c: any) =>
          Number(c.id),
        );

        const allRoleIds = [
          ...producerIds,
          ...presenterIds,
          ...coorganizerIds,
          ...cohostIds,
        ];

        const facultiesNotInRoles = facultiesIds.filter(
          (id: number) => !allRoleIds.includes(id),
        );
        const tutorsNotInRoles = tutorsIds.filter(
          (id: number) => !allRoleIds.includes(id),
        );

        if (
          (facultiesIds.length > 0 && facultiesNotInRoles.length > 0) ||
          (tutorsIds.length > 0 && tutorsNotInRoles.length > 0)
        ) {
          setOpenErrorRoleSession(true);
          return;
        }
      }
    }

    if (!canContinue) {
      setOpenErrorDateSession(true);
      return;
    }

    setErrorsessionForm(showError);
    const corporateDomainExist = columns.find(
      (item) => item.accessorKey === "corporate_domain",
    );

    if (corporateDomainExist !== undefined) {
      if (
        sessionForm["corporate_domain"] === "" ||
        sessionForm["corporate_domain"] === undefined
      ) {
        sessionForm["corporate_domain"] = null;
      }
    }

    sessionForm = { ...sessionForm, faculties: facultiesAssociation };

    if (canBeSubmit) {
      onSubmit(sessionForm);
    }
  };

  const getErrMsg = (accessorKey: string): string => {
    return errorsessionForm[accessorKey]?.msg || errorsessionForm[accessorKey];
  };

  const minLength = (accessorKey: string): number => {
    return errorsessionForm[accessorKey]?.value;
  };

  const maxLength = (accessorKey: string): number => {
    return errorsessionForm[accessorKey]?.value;
  };

  const getInputsList = (col: any, sessionForm: any) => {
    if (inputsException) {
      col = inputsException(col, sessionForm);
    }

    if (
      !!siteAssociation &&
      !siteAssociation.length &&
      col.accessorKey === "rooms"
    ) {
      col.disabled = true;
    }

    if (!!mainForm.publish_date && col.accessorKey === "start_date") {
      col.minDate = dayjs(mainForm.publish_date).format("YYYY-MM-DDTHH:mm:ssZ");
    }

    if (!!sessionForm.start_date && col.accessorKey === "end_date") {
      col.minDate = dayjs(sessionForm.start_date).format(
        "YYYY-MM-DDTHH:mm:ssZ",
      );
    }

    if (col.accessorKey === "online_meeting_updated_at") {
      return;
    }

    const fieldsToDisable = ["tool", "link", "site", "rooms", "QR-code"];

    if (
      !!Object.keys(row).length &&
      isPublishFuture !== undefined &&
      !isPublishFuture &&
      (endSession || (!isVirtual && (startSession24h || startSession))) &&
      fieldsToDisable.includes(col.accessorKey)
    ) {
      col.disabled = true;
    }

    if (
      (col.accessorKey === "start_date" || col.accessorKey === "end_date") &&
      (endSession || (!isVirtual && (startSession24h || startSession)))
    ) {
      col.disabled = true;
    }

    // Check if session is expired (end_date is in the past)
    const isSessionExpired =
      sessionForm.end_date && dayjs().isAfter(dayjs(sessionForm.end_date));

    if (isVirtual) {
      if (
        col.accessorKey === "trigger_external_services" &&
        mainForm.publish_date !== undefined
      ) {
        const isBeforePublish = dayjs().isBefore(dayjs(mainForm.publish_date));
        if (isBeforePublish) {
          sessionForm.trigger_external_services = false;
        }
        if (sessionForm.online_meeting_updated_at) {
          sessionForm.trigger_external_services = true;
        }
        col.disabled = isBeforePublish || isSessionExpired;
      }

      if (
        sessionForm.online_meeting_updated_at &&
        (col.accessorKey === "tool" ||
          col.accessorKey === "trigger_external_services")
      ) {
        col.disabled = true;
      }

      if (col.accessorKey === "confirmed") {
        col.disabled =
          (!sessionForm.trigger_external_services &&
            isVirtual &&
            sessionForm.tool !== "OTHER") ||
          row.session_confirmed ||
          !!confirmedDisabledTooltip ||
          isSessionExpired;
        if (
          mainForm.publish_date !== undefined &&
          sessionForm.tool === "OTHER"
        ) {
          const isBeforePublish = dayjs().isBefore(
            dayjs(mainForm.publish_date),
          );
          if (row.session_confirmed !== undefined && row.session_confirmed) {
            sessionForm.confirmed = true;
          } else if (isBeforePublish || isSessionExpired) {
            sessionForm.confirmed = false;
          }
          col.disabled ||= isBeforePublish;
        }
        if (
          !sessionForm.trigger_external_services &&
          isVirtual &&
          sessionForm.tool !== "OTHER"
        ) {
          sessionForm.confirmed = false;
        }
        if (sessionForm.online_meeting_updated_at && row.confirmed) {
          sessionForm.confirmed = true;
        }
      }
    }

    if (!isVirtual) {
      if (col.accessorKey === "confirmed") {
        col.disabled =
          row.session_confirmed ||
          !!confirmedDisabledTooltip ||
          isSessionExpired;
        if (mainForm.publish_date !== undefined) {
          const isBeforePublish = dayjs().isBefore(
            dayjs(mainForm.publish_date),
          );
          if (row.session_confirmed !== undefined && row.session_confirmed) {
            sessionForm.confirmed = true;
          } else if (isBeforePublish || isSessionExpired) {
            sessionForm.confirmed = false;
          }
          col.disabled ||= isBeforePublish;
        }
      }
    }

    switch (col.type) {
      case "association": {
        const siteValue = () => {
          const value = getValues && getValues(col.accessorKey);

          if (value.length === 0) {
            return [];
          } else if (value.id) {
            return getValues && [getValues(col.accessorKey)];
          } else {
            return getValues && getValues(col.accessorKey);
          }
        };
        return (
          <Grid key={col.accessorKey} item xs={12} sm={col.fullWidth ? 12 : 6}>
            <InputAssociationChipsCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={
                col.accessorKey === "site"
                  ? siteValue()
                  : getValues && getValues(col.accessorKey)
              }
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              noAssociationButton={col.disableAssociationButton}
              disabled={col.disabled}
              textTooltip={col.tooltip}
              required={col.required}
              onDelete={(e: any) => {
                handleDelete && handleDelete(col.accessorKey, e);
                setSessionForm((prev: any) => ({ ...prev }));
              }}
              onRedirect={() => {
                setOpenAssociation && setOpenAssociation(col.accessorKey, true);
              }}
              labelKey={col.labelKey}
            />
          </Grid>
        );
      }
      case "singleCheckbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SingleCheckboxCst
              key={col.accessorKey}
              id={col.accessorKey}
              value={sessionForm[col.accessorKey]}
              label={col.header}
              onChange={(e: any) => {
                setSessionForm({
                  ...sessionForm,
                  ...{ [col.accessorKey]: e.target.checked },
                });
              }}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "text": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={col.fullWidth ? 12 : 6}>
            <InputCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={sessionForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setSessionForm((prev: any) => ({
                  ...prev,
                  [col.accessorKey]: e.target.value,
                }));
                e.preventDefault();
              }}
              maxValue={col?.rules?.max?.value}
              multiline={col.multiline}
              rows={col.rows}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
              textTooltip={col.tooltip}
              type={col.inpuType || "text"}
            />
          </Grid>
        );
      }
      case "chips": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <InputChipsCst
              label={col.header}
              id={col.accessorKey}
              value={sessionForm[col.accessorKey]}
              required={col.required}
              maxLength={col?.rules?.max?.value}
              disabled={col.disabled}
              textTooltip={col.textTooltip}
              validationType={col.validationType}
              error={errorsessionForm[col.accessorKey] ? true : false}
              onAdd={(list: any[]) => {
                setSessionForm({
                  ...sessionForm,
                  ...{ [col.accessorKey]: list },
                });
              }}
              deleteDisabled={col.deleteDisabled}
              undeletableValues={col.undeletableValues}
            />
          </Grid>
        );
      }
      case "select": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <SelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              optionsToDisable={col.optionsToDisable}
              value={sessionForm[col.accessorKey]} //REFACTOR temp to show right value on select
              service={col.service}
              required={col.required}
              onChange={(e: any) => {
                if (col.callBack) {
                  setSessionForm(col.callBack(e, row));
                } else {
                  setSessionForm({
                    ...sessionForm,
                    ...{ [col.accessorKey]: e },
                  });
                }
              }}
              options={col?.options}
              textTooltip={col.tooltip}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "select-checkbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <MultiSelectCst
              id={col.accessorKey}
              label={col.header}
              optionsKey={col.optionsKey}
              value={sessionForm[col.accessorKey]}
              optionsToDisable={col.optionsToDisable}
              disabled={col.disabled}
              onChange={(e: any) => {
                let newValue = JSON.parse(JSON.stringify(e));
                let newsessionForm = JSON.parse(JSON.stringify(sessionForm));
                setSessionForm({
                  ...newsessionForm,
                  ...{ [col.accessorKey]: newValue },
                });
              }}
              options={col.options}
              required={col.required}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "autocomplete": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <AutocompleteCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              value={sessionForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setSessionForm({ ...sessionForm, ...{ [col.accessorKey]: e } });
              }}
              options={col?.options}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "multiCheckbox": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <MultiSelectCst
              key={col.accessorKey}
              id={col.accessorKey}
              optionsKey={col.optionsKey}
              label={col.header}
              value={sessionForm[col.accessorKey]}
              textTooltip={col.tooltip}
              required={col.required}
              service={col.service}
              onChange={(e: any) => {
                setSessionForm({ ...sessionForm, ...{ [col.accessorKey]: e } });
              }}
              options={col?.options}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "radio": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <RadioCst
              key={col.accessorKey}
              id={col.accessorKey}
              label={col.header}
              value={sessionForm[col.accessorKey]}
              onChange={(e: any) => {
                setSessionForm({ ...sessionForm, ...{ [col.accessorKey]: e } });
              }}
              options={col.options}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "dateTime": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DataPickerCst
              id={col.accessorKey}
              label={col.header}
              value={sessionForm[col.accessorKey]}
              required={col.required}
              minDate={col.minDate}
              onChange={(e: any) => {
                setSessionForm({ ...sessionForm, ...{ [col.accessorKey]: e } });
              }}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "dateTimeYear": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DataPickerCst
              views={["year"]}
              id={col.accessorKey}
              label={col.header}
              value={sessionForm[col.accessorKey]}
              required={col.required}
              onChange={(e: any) => {
                setSessionForm({ ...sessionForm, ...{ [col.accessorKey]: e } });
              }}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              disabled={col.disabled}
            />
          </Grid>
        );
      }
      case "time": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <TimePickerCst
              id={col.accessorKey}
              value={sessionForm[col.accessorKey]}
              error={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`) || "Error"
              }
              required={col.required}
              label={col.header}
              disabled={col.disabled}
              minDate={col.minDate || ""}
              onChange={(e: any) => {
                setSessionForm({ ...sessionForm, ...{ [col.accessorKey]: e } });
              }}
            />
          </Grid>
        );
      }
      case "multi-text": {
        return (
          <MultiTextCst
            key={col.accessorKey}
            label={col.header}
            fields={col.fields}
            value={sessionForm[col.accessorKey]}
            disabled={col.disabled}
            onChange={(e: any) => {
              setSessionForm({
                ...sessionForm,
                ...{ [col.accessorKey]: e },
              });
            }}
          />
        );
      }
      case "editor": {
        return (
          <TextEditorCst
            options={col.editorOptions}
            value={sessionForm[col.accessorKey]}
            onChange={(e: any) => {
              setSessionForm((prev: any) => {
                return {
                  ...prev,
                  [col.accessorKey]: e,
                };
              });
            }}
          />
        );
      }
      case "upload": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={6}>
            <DownloaderUploaderCst
              name={col.header}
              accept={col?.accept}
              url={col.service}
              type={col.upload_type}
              onChange={(e: any) => {
                setSessionForm({
                  ...sessionForm,
                  [col.accessorKey]: {
                    e,
                  },
                });
                setFileUploaded({
                  ...fileUploaded,
                  [col.accessorKey]: {
                    file: e,
                  },
                });
              }}
              fileName={!!row?.originalFileName && row.originalFileName}
              downloadDisabled={!!col.downloadDisabled}
              onDownload={col.downloadCallback && col.downloadCallback}
              onTemplateDownload={col.templateCallback && col.templateCallback}
              onDelete={
                col.deleteCallback &&
                (() => {
                  setFileUploaded(null);
                  return col.deleteCallback && col.deleteCallback();
                })
              }
              disabled={col.disabled}
              required={col?.required}
              showError={errorsessionForm[col.accessorKey] ? true : false}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
            />
          </Grid>
        );
      }
      case "switch": {
        return (
          <Grid key={col.accessorKey} item xs={12} sm={col.fullWidth ? 12 : 6}>
            <SwitchCst
              label={col.header}
              id={col.accessorKey}
              textTooltip={col.tooltip}
              disabled={col.disabled}
              disabledTooltip={
                col.accessorKey === "confirmed" && col.disabled
                  ? confirmedDisabledTooltip
                  : undefined
              }
              value={!!sessionForm[col.accessorKey]}
              errorMessage={
                t(`error.field.${getErrMsg(col.accessorKey)}`, {
                  min: minLength(col.accessorKey),
                  max: maxLength(col.accessorKey),
                }) || "Error"
              }
              error={errorsessionForm[col.accessorKey] ? true : false}
              onChange={(e: any) => {
                if (col.callBack) {
                  setSessionForm(col.callBack(e.target.checked, row));
                } else {
                  setSessionForm({
                    ...sessionForm,
                    [col.accessorKey]: e.target.checked,
                  });
                }
              }}
            />
          </Grid>
        );
      }
      default:
        return <></>;
    }
  };

  const getInputField = useMemo(() => {
    return columns?.map((col: any) => {
      if (!!col.knowledge) {
        return false;
      }

      if (col.onlyfor_type) {
        return false;
      }

      return getInputsList(col, sessionForm);
    });
  }, [
    sessionForm,
    siteAssociation,
    roomAssociation,
    facultiesAssociation,
    tutorsAssociation,
  ]);

  useEffect(() => {
    // Simula una modifica di sessionForm ogni volta che cambia mainForm o sessionForm
    // nella pratica serv per refrescare il componente er fare vedere gli errori
    setSessionForm((prev: any) => ({
      ...prev,
      mainFormChangedAt: Date.now(),
    }));
  }, [mainForm, errorsessionForm, sessionForm.trigger_external_services]);

  const getRolesField = useMemo(() => {
    return columns?.map((col: any) => {
      const toolType = sessionForm["tool"];
      const onlyForType = col["onlyfor_type"];

      if (isVirtual && onlyForType) {
        if (onlyForType.includes("ROLES") && onlyForType.includes(toolType)) {
          return getInputsList(col, sessionForm);
        }
      }

      return false;
    });
  }, [sessionForm]);

  const getLobbyField = useMemo(() => {
    return columns?.map((col: any) => {
      const toolType = sessionForm["tool"];
      const onlyForType = col["onlyfor_type"];

      if (isVirtual && onlyForType) {
        if (col.accessorKey === "scope") {
          col.required = toolType === "MSTEAMS";
        }

        if (col.accessorKey === "access_type") {
          col.required = toolType === "GMEET";
        }

        if (onlyForType.includes("LOBBY") && onlyForType.includes(toolType)) {
          return getInputsList(col, sessionForm);
        }
      }

      return false;
    });
  }, [sessionForm]);

  const getGeneralField = useMemo(() => {
    return columns?.map((col: any) => {
      const toolType = sessionForm["tool"];
      const onlyForType = col["onlyfor_type"];

      if (isVirtual && onlyForType) {
        if (col.accessorKey === "allow_meeting_chat") {
          col.required = toolType === "MSTEAMS";
        }

        if (col.accessorKey === "allowed_presenters") {
          col.required = toolType === "MSTEAMS";
        }

        if (col.accessorKey === "link") {
          col.required = toolType === "OTHER";
        }

        if (col.accessorKey === "chat_restriction") {
          col.required = toolType === "GMEET";
        }

        if (onlyForType.includes("GENERAL") && onlyForType.includes(toolType)) {
          return getInputsList(col, sessionForm);
        }
      }

      return false;
    });
  }, [sessionForm]);

  const getInviteField = useMemo(() => {
    return columns?.map((col: any) => {
      const toolType = sessionForm["tool"];
      const onlyForType = col["onlyfor_type"];

      if (isVirtual && onlyForType) {
        if (onlyForType.includes("INVITE") && onlyForType.includes(toolType)) {
          return getInputsList(col, sessionForm);
        }
      }

      return false;
    });
  }, [sessionForm]);

  const getLinkField = useMemo(() => {
    return columns?.map((col: any) => {
      const toolType = sessionForm["tool"];
      const onlyForType = col["onlyfor_type"];

      if (isVirtual && onlyForType) {
        if (onlyForType.includes("LINK") && onlyForType.includes(toolType)) {
          return getInputsList(col, sessionForm);
        }
      }

      return false;
    });
  }, [sessionForm]);

  useEffect(() => {
    if (!siteAssociation?.length) {
      delete sessionForm.site;
    }

    if (!roomAssociation?.length) {
      delete sessionForm.rooms;
    }

    if (!facultiesAssociation?.length) {
      sessionForm.faculties = facultiesAssociation;
    }

    if (!tutorsAssociation?.length) {
      sessionForm.tutors = tutorsAssociation;
    }

    if (!producerAssociation?.length) {
      sessionForm.producer = producerAssociation;
    }

    if (!presenterAssociation?.length) {
      sessionForm.presenter = presenterAssociation;
    }

    if (!coorganizerAssociation?.length) {
      sessionForm.coorganizer = coorganizerAssociation;
    }

    if (!cohostAssociation?.length) {
      sessionForm.cohost = cohostAssociation;
    }
  }, [
    sessionForm,
    siteAssociation,
    roomAssociation,
    facultiesAssociation,
    tutorsAssociation,
    producerAssociation,
    presenterAssociation,
    coorganizerAssociation,
    cohostAssociation,
  ]);

  const showConfirmed =
    (isVirtual && sessionForm.tool === "OTHER") || !isVirtual;

  return (
    <>
      <Box
        sx={{
          mt: "1.5rem",
          border: `1px solid ${theme.palette.text.tertiary}`,
          borderRadius: "2px",
          padding: "1rem",
        }}
      >
        <Typography
          sx={{
            mb: "1.5rem",
            fontWeight: 400,
            fontSize: "1rem",
          }}
        >
          {t("session-form.session-label")}{" "}
          {editIndex ? editIndex : sessionsCount}
        </Typography>
        <Grid container spacing={3}>
          {isVirtual &&
            ["MSTEAMS", "GMEET"].includes(sessionForm.tool) &&
            sessionForm?.online_meeting_updated_at && (
              <Grid item xs={12}>
                <Alert
                  variant="outlined"
                  severity="info"
                  sx={{
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.text.tertiary,
                    "& .MuiAlert-icon": {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  {t("session-form.meeting-sent-alert").replace(
                    "%date",
                    dayjs(sessionForm.online_meeting_updated_at)
                      .utc()
                      .format("DD-MM-YYYY HH:mm"),
                  )}
                </Alert>
                <Alert
                  variant="outlined"
                  severity="warning"
                  sx={{
                    mt: 2,
                    color: theme.palette.text.primary,
                    borderColor: theme.palette.text.tertiary,
                    "& .MuiAlert-icon": {
                      color: theme.palette.text.primary,
                    },
                  }}
                >
                  {t("session-form.meeting-sent-limit")}
                </Alert>
              </Grid>
            )}
          {getInputField}
          <Grid item xs={12}>
            {isVirtual && ["MSTEAMS", "GMEET"].includes(sessionForm.tool) && (
              <>
                <MeetingSection title={t("session-form.meeting-roles")}>
                  {getRolesField}
                </MeetingSection>
                <MeetingSection title={t("session-form.meeting-lobby")}>
                  {getLobbyField}
                </MeetingSection>
                <MeetingSection title={t("session-form.meeting-general")}>
                  {getGeneralField}
                </MeetingSection>
                <Grid item xs={12} sx={{ mt: 3 }}>
                  {sessionForm?.online_meeting_updated_at && (
                    <MeetingSection title={t("session-form.link")}>
                      {getLinkField}
                    </MeetingSection>
                  )}
                </Grid>
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    {getInviteField}
                  </Grid>
                </Grid>
              </>
            )}
            {showConfirmed && (
              <>
                <Grid item xs={12}>
                  {getGeneralField}
                </Grid>
                <Grid item xs={12} sx={{ mt: 3 }}>
                  <Grid container spacing={3}>
                    {getInviteField}
                  </Grid>
                </Grid>
              </>
            )}
          </Grid>
        </Grid>
        <Grid container spacing={3}>
          <Grid
            item
            xs={12}
            sx={{
              mt: "2rem",
              mr: "1rem",
              display: "flex",
              alignItems: "center",
              justifyContent: "flex-end",
              gap: "20px",
              [theme.breakpoints.down("sm")]: {
                flexDirection: "column",
              },
            }}
          >
            <IconButton
              aria-label="delete session"
              onClick={!!Object.keys(row).length ? onResetEdit : onReset}
              edge="end"
              disabled={isNew}
            >
              <Delete />
            </IconButton>
            <IconButton
              aria-label="save session"
              onClick={onConfirm}
              edge="end"
            >
              <Save />
            </IconButton>
          </Grid>
        </Grid>
      </Box>
      <ModalCst
        open={openErrorDatesSession}
        setOpen={setOpenErrorDateSession}
        typeOfModal={"enrollmentExceeded"}
        title={t("session-form.session-already-exist") || ""}
        description={t("session-form.session-date-error") || ""}
      />
      <ModalCst
        open={openErrorRoleSession}
        setOpen={setOpenErrorRoleSession}
        typeOfModal={"enrollmentExceeded"}
        title={t("session-form.session-alert-role") || ""}
        description={t("session-form.session-alert-role-error") || ""}
      />
    </>
  );
};

export default SessionEditionFormCst;
